"use client";
import React from "react";
import { ThemeUICSSObject } from "theme-ui";

import { Box, BoxDiv, BoxSx } from "..";
import { useThemeWithoutTypes } from "../Theme";
import { GridSizeArray } from "./GridTypes";

type GridColumnElementProps = {
  fillEnd: boolean;
  fillGapEnd: boolean;
  fillGapStart: boolean;
  fillStart: boolean;
  parentSize: GridSizeArray;
  push: GridSizeArray;
  size: GridSizeArray;
} & Omit<BoxDiv, "size">;

export const getWithoutZeroDecimal = (num: number) => {
  return num.toFixed(6).replace(/0*$/, "").replace(/\.$/, "");
};

export const getMarginInlineEnd = (
  maxWidth: number,
  gutterWidth: number,
  marginWidth: number,
  fillGapEnd = false,
  fill?: boolean
) => {
  const extraGutterWidth = fillGapEnd ? -(gutterWidth / 2) : gutterWidth / 2;

  return fill
    ? `calc(min(-50vw + ${maxWidth / 2}px, ${-(
        marginWidth - gutterWidth
      )}px) + ${extraGutterWidth - gutterWidth}px)`
    : `${extraGutterWidth}px`;
};

export const getMarginInlineStart = (
  maxWidth: number,
  columnCount: number,
  gutterWidth: number,
  marginWidth: number,
  push: number,
  fillGapStart = false,
  fill?: boolean
) => {
  const baseGutterWidth = gutterWidth / 2;
  const extraGutterWidth = fillGapStart ? gutterWidth : 0;
  const pushWidth = push
    ? `${getWithoutZeroDecimal((push / columnCount) * 100)}% + `
    : "";

  if (fill) {
    return `calc(${pushWidth}min(-50vw + ${maxWidth / 2 + gutterWidth}px, -${
      marginWidth - gutterWidth
    }px) + ${-baseGutterWidth}px)`;
  }

  return !push
    ? `${baseGutterWidth - extraGutterWidth}px`
    : `calc(${pushWidth}${baseGutterWidth - extraGutterWidth}px)`;
};

export const getPadding = (
  maxWidth: number,
  gutterWidth: number,
  marginWidth: number,
  fillGap = false,
  fill?: boolean
) => {
  return fill
    ? `calc(max(50vw - ${maxWidth / 2 + gutterWidth}px, ${
        marginWidth - gutterWidth
      }px) + ${gutterWidth - (fillGap ? gutterWidth : 0)}px)`
    : "0";
};

export const getWidth = (
  columnCount: number,
  gutterWidth: number,
  size: number,
  fillGapStart = false,
  fillGapEnd = false
) => {
  const extraGutterWidth =
    -gutterWidth +
    (fillGapStart ? gutterWidth : 0) +
    (fillGapEnd ? gutterWidth : 0);

  return `calc(${getWithoutZeroDecimal(
    Math.min(size / columnCount, 1) * 100
  )}%${extraGutterWidth === 0 ? "" : ` + ${extraGutterWidth}px`})`;
};

export const getDefinedSize = (
  sizes: GridSizeArray,
  startSize: number,
  maxSize: number
): number => {
  for (let i = startSize; i < sizes.length; i++) {
    const sizeCurrent = sizes[i];

    if (sizeCurrent !== null && sizeCurrent !== undefined) {
      return sizeCurrent;
    }
  }

  return maxSize;
};

export const GridColumnElement = React.forwardRef<
  HTMLElement,
  GridColumnElementProps
>(
  (
    {
      fillEnd,
      fillGapEnd,
      fillGapStart,
      fillStart,
      parentSize,
      push,
      size,
      sx,
      ...props
    },
    ref
  ) => {
    const theme = useThemeWithoutTypes();
    const styles = React.useMemo<ThemeUICSSObject>(
      () => ({
        boxSizing: "initial",
        flexShrink: 0,
        marginInlineEnd: [
          getMarginInlineEnd(
            theme.sizes.containerMobileMaxWidth,
            theme.sizes.gridMobileColumnGutter,
            theme.sizes.containerMobileMargin,
            fillGapEnd,
            fillEnd
          ),
          getMarginInlineEnd(
            theme.sizes.containerTabletMaxWidth,
            theme.sizes.gridTabletColumnGutter,
            theme.sizes.containerTabletMargin,
            fillGapEnd,
            fillEnd
          ),
          getMarginInlineEnd(
            theme.sizes.containerDesktopMaxWidth,
            theme.sizes.gridDesktopColumnGutter,
            theme.sizes.containerDesktopMargin,
            fillGapEnd,
            fillEnd
          )
        ],
        marginInlineStart: [
          getMarginInlineStart(
            theme.sizes.containerMobileMaxWidth,
            parentSize[0] || theme.grid.gridMobileColumnCount,
            theme.sizes.gridMobileColumnGutter,
            theme.sizes.containerMobileMargin,
            getDefinedSize(push, 0, 0),
            fillGapStart,
            fillStart
          ),
          getMarginInlineStart(
            theme.sizes.containerTabletMaxWidth,
            parentSize[1] || theme.grid.gridTabletColumnCount,
            theme.sizes.gridTabletColumnGutter,
            theme.sizes.containerTabletMargin,
            getDefinedSize(push, 1, 0),
            fillGapStart,
            fillStart
          ),
          getMarginInlineStart(
            theme.sizes.containerDesktopMaxWidth,
            parentSize[2] || theme.grid.gridDesktopColumnCount,
            theme.sizes.gridDesktopColumnGutter,
            theme.sizes.containerDesktopMargin,
            getDefinedSize(push, 2, 0),
            fillGapStart,
            fillStart
          )
        ],
        paddingInlineEnd: [
          getPadding(
            theme.sizes.containerMobileMaxWidth,
            theme.sizes.gridMobileColumnGutter,
            theme.sizes.containerMobileMargin,
            fillGapEnd,
            fillEnd
          ),
          getPadding(
            theme.sizes.containerTabletMaxWidth,
            theme.sizes.gridTabletColumnGutter,
            theme.sizes.containerTabletMargin,
            fillGapEnd,
            fillEnd
          ),
          getPadding(
            theme.sizes.containerDesktopMaxWidth,
            theme.sizes.gridDesktopColumnGutter,
            theme.sizes.containerDesktopMargin,
            fillGapEnd,
            fillEnd
          )
        ],
        paddingInlineStart: [
          getPadding(
            theme.sizes.containerMobileMaxWidth,
            theme.sizes.gridMobileColumnGutter,
            theme.sizes.containerMobileMargin,
            fillGapStart,
            fillStart
          ),
          getPadding(
            theme.sizes.containerTabletMaxWidth,
            theme.sizes.gridTabletColumnGutter,
            theme.sizes.containerTabletMargin,
            fillGapStart,
            fillStart
          ),
          getPadding(
            theme.sizes.containerDesktopMaxWidth,
            theme.sizes.gridDesktopColumnGutter,
            theme.sizes.containerDesktopMargin,
            fillGapStart,
            fillStart
          )
        ],
        position: "relative",
        width: [
          getWidth(
            parentSize[0] || theme.grid.gridMobileColumnCount,
            theme.sizes.gridMobileColumnGutter,
            getDefinedSize(size, 0, theme.grid.gridMobileColumnCount),
            fillGapStart,
            fillGapEnd
          ),
          getWidth(
            parentSize[1] || theme.grid.gridTabletColumnCount,
            theme.sizes.gridTabletColumnGutter,
            getDefinedSize(size, 1, theme.grid.gridTabletColumnCount),
            fillGapStart,
            fillGapEnd
          ),
          getWidth(
            parentSize[2] || theme.grid.gridDesktopColumnCount,
            theme.sizes.gridDesktopColumnGutter,
            getDefinedSize(size, 2, theme.grid.gridDesktopColumnCount),
            fillGapStart,
            fillGapEnd
          )
        ]
      }),
      [
        fillEnd,
        fillGapEnd,
        fillGapStart,
        fillStart,
        parentSize,
        push,
        size,
        theme
      ]
    );

    return (
      <Box
        ref={ref}
        sx={
          {
            display: "flex",
            flexDirection: "column",
            ...sx,
            ...styles
            // This element requires calculated sizing values and can't use
            // the Box sx props from the theme.
          } as unknown as BoxSx
        }
        {...props}
      />
    );
  }
);

GridColumnElement.displayName = "GridColumnElement";
